/**
 * Sector operations — Asset Actions Pure Helper (R39 A8, SO-P67).
 * Computes allowed user actions on an ops asset based on viewer role,
 * sector roles, asset status, open requests, and repairer history.
 */

import type { WorkspaceRole } from '../types/workspace'
import type { OpsSectorRole, OpsAssetStatus } from '../types/ops'

export interface AssetViewerContext {
  uid: string
  role?: WorkspaceRole | null
  sectorRoles?: readonly OpsSectorRole[] | null
}

export interface AssetActionsOptions {
  hasOpenRequest?: boolean
  lastDoneByUid?: string | null
}

export interface AssetForActions {
  status: OpsAssetStatus
  retired?: boolean
}

export interface AssetActionsResult {
  canEdit: boolean
  canSetStatus: boolean
  canIsolate: boolean
  canRelease: boolean
  canSelfRelease: boolean
  canRetire: boolean
  canRaiseFault: boolean
  releaseBlockedReason?: 'REQUEST_OPEN' | 'REPAIRER_CANNOT_RELEASE'
}

/**
 * Pure helper determining who can perform which actions on an asset.
 */
export function assetActionsFor(
  asset: AssetForActions,
  viewer: AssetViewerContext,
  options?: AssetActionsOptions,
): AssetActionsResult {
  if (asset.retired || asset.status === 'RETIRED') {
    return {
      canEdit: false,
      canSetStatus: false,
      canIsolate: false,
      canRelease: false,
      canSelfRelease: false,
      canRetire: false,
      canRaiseFault: false,
    }
  }

  const isOwner = viewer.role === 'OWNER'
  const isAdmin = viewer.role === 'ADMIN'
  const isEditor = viewer.role === 'EDITOR'
  const isElevated = isOwner || isAdmin || isEditor
  const hasReleaserRole = Boolean(viewer.sectorRoles?.includes('ASSET_RELEASER'))
  const isReleaserOrAdmin = isOwner || isAdmin || hasReleaserRole

  const canEdit = isElevated
  const canRetire = isElevated
  const canSetStatus = isElevated && (asset.status === 'AVAILABLE' || asset.status === 'IN_USE')
  const canIsolate = isReleaserOrAdmin && asset.status !== 'ISOLATED'
  const canRaiseFault = Boolean(viewer.uid)

  let canRelease = false
  let canSelfRelease = false
  let releaseBlockedReason: 'REQUEST_OPEN' | 'REPAIRER_CANNOT_RELEASE' | undefined

  const isReleasableStatus = asset.status === 'ISOLATED' || asset.status === 'MAINTENANCE'

  if (isReleasableStatus && isReleaserOrAdmin) {
    if (options?.hasOpenRequest === true) {
      canRelease = false
      canSelfRelease = false
      releaseBlockedReason = 'REQUEST_OPEN'
    } else {
      const isRepairer = Boolean(
        viewer.uid && options?.lastDoneByUid && options.lastDoneByUid === viewer.uid,
      )

      if (isRepairer) {
        if (isOwner) {
          canRelease = false
          canSelfRelease = true
        } else {
          canRelease = false
          canSelfRelease = false
          releaseBlockedReason = 'REPAIRER_CANNOT_RELEASE'
        }
      } else {
        canRelease = true
        canSelfRelease = false
      }
    }
  }

  return {
    canEdit,
    canSetStatus,
    canIsolate,
    canRelease,
    canSelfRelease,
    canRetire,
    canRaiseFault,
    ...(releaseBlockedReason ? { releaseBlockedReason } : {}),
  }
}
